import { promises as fs } from 'fs';

import GlobalLayout from 'layout';
import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import { PageProps } from 'types/global';

import { fetchBuilderDataModel, fetchLayoutModels } from 'utils/builder/builder.sdk';
import { getConnectorData } from 'utils/fetch/fetchConnectorsData';

// Register the Custom Card / Switchback blocks used inside the symbols these
// pages render, so the raw <BuilderComponent> can resolve them (otherwise they
// render empty with a "Missing registration" warning).
import 'utils/builder/registerSymbolComponents';

import type { Content } from '@builder.io/react';

const Application = dynamic(() => import('template/connectors/Application'));
const Helper = dynamic(() => import('template/connectors/Helper'));

const SYMBOL_IDS = {
  application: {
    primary: '555d351dd2db4977bc18c031ad652785',
    secondary: '9b0e0cacb43849e0bb12092352965e8c',
  },
  helper: {
    primary: '2b050004421f4dc1b5808eae62123188',
    secondary: 'acf23deca02c436fba8581ea4271823d',
  },
} as const;

const getConnectorTemplate = (category: string) => {
  switch (category) {
    case 'Application Connectors':
    case 'Data Platforms':
      return 'application';
    case 'Logic':
    case 'Helpers':
    case 'Triggers':
    default:
      return 'helper';
  }
};

interface ConnectorPageProps extends PageProps {
  data: Connector;
  symbolContent: object | null;
  symbolContentSecondary: object | null;
}

const Components = ({ data, preview, layoutData, symbolContent, symbolContentSecondary }: ConnectorPageProps) => {
  if (!data?.display?.label) {
    return null;
  }

  const template = getConnectorTemplate(data.display.category);
  const titleHero = data.builderExtras?.titleHero?.trim();
  const seoTitle = titleHero || `${data.display.label} ${template === 'application' ? 'Connector' : 'Component'}`;

  return (
    <GlobalLayout
      preview={preview}
      seoData={
        {
          title: seoTitle,
          description: data.display.description,
          url: `/connectors/${data.key}/`,
        } as unknown as Content
      }
      {...layoutData}
    >
      {template === 'application' ? (
        <Application data={data} symbolContent={symbolContent} symbolContentSecondary={symbolContentSecondary} />
      ) : (
        <Helper data={data} symbolContent={symbolContent} symbolContentSecondary={symbolContentSecondary} />
      )}
    </GlobalLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const file = await fs.readFile(process.cwd() + '/src/data/connectorPaths.json', 'utf8');
  const connectors = JSON.parse(file) as { key: string; path: string }[];

  return {
    paths: connectors.filter(connector => connector.key).map(connector => ({ params: { slug: connector.key } })),
    fallback: 'blocking',
  };
};

const fetchSymbolContent = async (symbolId: string): Promise<object | null> => {
  try {
    const result = await fetchBuilderDataModel('symbol', {
      query: { id: symbolId },
      singleItem: true,
    });

    return result ?? null;
  } catch {
    return null;
  }
};

export const getStaticProps: GetStaticProps = async ({ params, preview }) => {
  const slug = params?.slug as string;

  try {
    const data = await getConnectorData(slug);
    const template = getConnectorTemplate(data.display?.category);
    const symbolIds = SYMBOL_IDS[template];

    const [layoutData, symbolContent, symbolContentSecondary] = await Promise.all([
      // `preview` here is Next.js Preview Mode (cookie-gated via
      // /api/draft-preview); preview renders are served on demand and never
      // written to the ISR cache, so draft chrome stays out of the static pages.
      fetchLayoutModels(preview, !!preview),
      fetchSymbolContent(symbolIds.primary),
      fetchSymbolContent(symbolIds.secondary),
    ]);

    // Strip heavy fields not used by the template to reduce page data payload
    const trimmedData = {
      key: data.key ?? null,
      display: {
        label: data.display?.label ?? null,
        description: data.display?.description ?? null,
        category: data.display?.category ?? null,
        iconBase64: data.display?.iconBase64 ?? null,
        marketingCategories: data.display?.marketingCategories ?? null,
      },
      documentationUrl: data.documentationUrl ?? null,
      actions: Object.fromEntries(
        Object.entries(data.actions || {}).map(([key, action]) => [
          key,
          {
            display: {
              label: (action as { display?: { label?: string } }).display?.label ?? '',
              description: (action as { display?: { description?: string } }).display?.description ?? '',
            },
          },
        ]),
      ),
      triggers: Object.fromEntries(
        Object.entries(data.triggers || {}).map(([key, trigger]) => [
          key,
          {
            display: {
              label: (trigger as { display?: { label?: string } }).display?.label ?? '',
              description: (trigger as { display?: { description?: string } }).display?.description ?? '',
            },
          },
        ]),
      ),
      builderExtras: data.builderExtras ?? null,
    };

    return {
      props: {
        data: trimmedData,
        layoutData,
        symbolContent,
        symbolContentSecondary,
        preview: preview || false,
      },
      revalidate: 60,
    };
  } catch (err) {
    console.error(err);

    return { notFound: true };
  }
};

export default Components;
